import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ImageEditor } from '@ciancio/shared';
import { CodePanel } from '../components/upload/CodePanel';
import { ImageGrid } from '../components/upload/ImageGrid';
import { UploadAlert } from '../components/upload/UploadAlert';
import { UploadControls } from '../components/upload/UploadControls';
import { UploadProgress } from '../components/upload/UploadProgress';
import { createUploadSession, finalizeUpload } from '../services/uploadService';
import { defaultEditState } from '@ciancio/shared';

const MAX_FILES = 2000;

export const UploadsPage = ({ onLastCodeChange }) => {
  const [images, setImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [uploadState, setUploadState] = useState({
    status: 'idle',
    progress: 0,
    code: null,
    error: null,
    completedImages: 0,
    currentImage: 0,
    totalImages: 0,
    startedAt: null,
    totalBytes: 0,
    uploadedBytes: 0,
    tick: 0,
  });
  const editingImage = useMemo(() => images.find((image) => image.id === editingId), [images, editingId]);

  useEffect(() => {
    if (uploadState.status !== 'uploading') {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploadState.status]);

  useEffect(() => {
    if (uploadState.status !== 'uploading') {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setUploadState((current) => ({ ...current, tick: current.tick + 1 }));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [uploadState.status]);

  const handleSelectFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      return;
    }

    setImages((current) => [
      ...current,
      ...selectedFiles.slice(0, MAX_FILES - current.length).map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        editedUrl: null,
        editedBlob: null,
        editState: defaultEditState,
      })),
    ]);
    event.target.value = '';
  };

  const handleRemove = (id) => {
    setImages((current) => current.filter((image) => image.id !== id));
  };

  const handleSaveEdit = (id, editState, exported) => {
    setImages((current) => current.map((image) => {
      if (image.id !== id) {
        return image;
      }

      if (image.editedUrl) {
        URL.revokeObjectURL(image.editedUrl);
      }

      return {
        ...image,
        editState,
        editedUrl: exported.url,
        editedBlob: exported.blob,
      };
    }));
    setEditingId(null);
  };

  const handleConfirmUpload = async () => {
    setConfirming(false);
    setUploadState({
      status: 'uploading',
      progress: 0,
      code: null,
      error: null,
      completedImages: 0,
      currentImage: images.length ? 1 : 0,
      totalImages: images.length,
      startedAt: Date.now(),
      totalBytes: getTotalUploadBytes(images),
      uploadedBytes: 0,
      tick: 0,
    });

    try {
      const session = await createUploadSession();
      const completeSession = await finalizeUpload(session, images, (progressState) => {
        setUploadState((current) => ({ ...current, ...progressState }));
      });
      setImages([]);
      onLastCodeChange(completeSession.code);
      setUploadState({
        status: 'complete',
        progress: 100,
        code: completeSession.code,
        error: null,
        completedImages: images.length,
        currentImage: images.length,
        totalImages: images.length,
        startedAt: null,
        totalBytes: 0,
        uploadedBytes: 0,
        tick: 0,
      });
    } catch (error) {
      setUploadState({
        status: 'error',
        progress: 0,
        code: null,
        error: error.message,
        completedImages: 0,
        currentImage: 0,
        totalImages: 0,
        startedAt: null,
        totalBytes: 0,
        uploadedBytes: 0,
        tick: 0,
      });
    }
  };

  const resetUpload = () => {
    setUploadState({
      status: 'idle',
      progress: 0,
      code: null,
      error: null,
      completedImages: 0,
      currentImage: 0,
      totalImages: 0,
      startedAt: null,
      totalBytes: 0,
      uploadedBytes: 0,
      tick: 0,
    });
  };

  return (
    <>
      <UploadAlert message={uploadState.error} visible={uploadState.status === 'error'} />

      <div className="container top-container mt-2">
        <UploadProgress
          completedImages={uploadState.completedImages}
          currentImage={uploadState.currentImage}
          progress={uploadState.progress}
          startedAt={uploadState.startedAt}
          totalBytes={uploadState.totalBytes}
          totalImages={uploadState.totalImages}
          uploadedBytes={uploadState.uploadedBytes}
          visible={uploadState.status === 'uploading'}
        />
        <ImageGrid hidden={uploadState.status === 'complete'} images={images} onEdit={setEditingId} onRemove={handleRemove} />
        <CodePanel code={uploadState.code} visible={uploadState.status === 'complete'} onReset={resetUpload} />
      </div>

      <UploadControls
        hidden={uploadState.status === 'complete'}
        imageCount={images.length}
        onSelectFiles={handleSelectFiles}
        onUpload={() => setConfirming(true)}
        uploading={uploadState.status === 'uploading'}
      />

      {confirming && <ConfirmDialog count={images.length} onCancel={() => setConfirming(false)} onConfirm={handleConfirmUpload} />}
      {editingImage && <ImageEditor image={editingImage} onCancel={() => setEditingId(null)} onSave={handleSaveEdit} />}
    </>
  );
}

function getTotalUploadBytes(images) {
  return images.reduce((sum, image) => {
    return sum + (image.file?.size || 0) + (image.editedBlob?.size || 0);
  }, 0);
}

